import { useNavigate } from '@tanstack/react-router'
import { Challenge, Category } from '../services/adminService'
import { formatDate } from '../utils/dateUtils'
import { Table, TableColumn } from './Table'
import { Switch } from './ui/Switch'
import { useTranslation } from '../i18n/hooks/useTranslation'
import { Button } from './ui/Button'

interface ChallengeListProps {
  challenges: Challenge[]
  categories: Category[]
  onTranslations: (id: string) => void
  onToggleStatus: (challenge: Challenge) => void
  togglingChallengeId?: string | null
}

export function ChallengeList({ challenges, categories, onToggleStatus, togglingChallengeId }: ChallengeListProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const getStatusBadge = (status: string) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800', 
      draft: 'bg-yellow-100 text-yellow-800'
    }
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status as keyof typeof styles] || styles.active}`}>
        {t(`common.${status}`)}
      </span>
    )
  }

  const getCategoryName = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId)
    return category?.name || categoryId
  }

  // Generate category options for filter
  const categoryOptions = categories.map(category => ({
    value: category.id,
    label: `${category.icon} ${category.name}`
  }))

  // Status options for filter
  const statusOptions = [
    { value: 'active', label: t('common.active') },
    { value: 'inactive', label: t('common.inactive') },
    { value: 'draft', label: t('common.draft') }
  ]

  // Frequency options for filter
  const frequencyOptions = [
    { value: 'daily', label: t('challenges.fields.daily') },
    { value: 'hourly', label: t('challenges.fields.hourly') },
    { value: 'weekly', label: t('challenges.fields.weekly') },
    { value: 'not set', label: t('challenges.fields.notSet') }
  ]

  const columns: TableColumn<Challenge>[] = [
    {
      key: 'status',
      header: t('common.status'),
      sortKey: 'status',
      sortable: true,
      className: 'w-40',
      filterable: {
        type: 'select',
        options: statusOptions,
        filterKey: 'status'
      },
      render: (challenge: Challenge) => (
        <Switch
          key={`switch-${challenge.id}`}
          checked={challenge.status === 'active'}
          onChange={() => onToggleStatus(challenge)}
          disabled={togglingChallengeId === challenge.id}
          size="sm"
        />
      )
    },
    {
      key: 'challenge',
      header: t('challenges.title').slice(0, -1),
      sortKey: 'name',
      sortable: true,
      className: 'max-w-xs',
      filterable: {
        type: 'text',
        placeholder: t('filters.filterBy.name'),
        filterKey: 'name'
      },
      render: (challenge: Challenge) => (
        <div className="flex items-center">
          {challenge.icon && (
            <span className="text-lg mr-3">{challenge.icon}</span>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-gray-900 truncate">
              {challenge.name || challenge.id}
            </div>
            {challenge.description && (
              <div className="text-sm text-gray-500 truncate mt-0.5">
                {challenge.description}
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'category',
      header: t('categories.title').slice(0, -1),
      sortKey: 'category',
      sortable: true,
      filterable: {
        type: 'select',
        options: categoryOptions,
        filterKey: 'category'
      },
      render: (challenge: Challenge) => (
        <span className="text-sm text-gray-900">
          {getCategoryName(challenge.category)}
        </span>
      )
    },
    {
      key: 'frequency',
      header: t('challenges.fields.defaultFrequency'),
      sortKey: 'defaultFrequency',
      sortable: true,
      filterable: {
        type: 'select',
        options: frequencyOptions,
        filterKey: 'defaultFrequency'
      },
      render: (challenge: Challenge) => (
        <span className="text-sm text-gray-900">
          {challenge.defaultFrequency?.mode ? t(`challenges.fields.${challenge.defaultFrequency.mode}`) : t('challenges.fields.notSet')}
        </span>
      )
    },

    {
      key: 'actions',
      header: t('common.actions'),
      className: 'text-right w-32',
      filterable: {
        type: 'none'
      },
      render: (challenge: Challenge) => (
        <div className="flex space-x-2">
          <Button
            onClick={() => {
              console.log('🔗 Navigating to edit challenge with ID:', challenge.id)
              console.log('🔗 Full challenge object:', challenge)
              navigate({ to: '/challenges/$challengeId', params: { challengeId: challenge.id } })
            }}
            variant="primary"
            size="sm"
          >
            {t('common.edit')}
          </Button>
        </div>
      )
    }
  ]

  return (
            <Table
      data={challenges}
      columns={columns}
      searchableFields={['name', 'description']}
      searchPlaceholder={t('challenges.searchPlaceholder')}
      emptyMessage={t('challenges.noChallengesFound')}
      emptySubMessage={t('challenges.createFirstChallenge')}
      pageSize={10}
      t={t}
    />
  )
}